import { useCallback } from "react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import { type UIPrice } from "@flights/types";

import useTrackerContext from "../useTrackerContext";
import useGlobalContext from "../useGlobalContext";
import useRouteName from "../useRouteName";
import { type RouteName } from "@flights/constants";

const ACTION_NAME = "flights__price_rendered";
const ACTION_VERSION = "2.0.0";

export type RouteNameWithPaymentPriceChange = RouteName | "checkout-payment-price-change";

export type PriceRenderedUiComponent = "BREAKDOWN" | "TAG";

export type PriceRenderedProductPrice = {
  productType: string;
  productPrice: UIPrice;
};

type PriceRenderedPrice = {
  currency_code?: string;
  price?: string;
};

type PriceRenderedProduct = {
  price?: PriceRenderedPrice;
  product_id?: string;
  product_type?: string;
};

type PriceRenderedContent = {
  funnel_page: RouteNameWithPaymentPriceChange;
  products?: PriceRenderedProduct[];
  request_id: string;
  total_price: PriceRenderedPrice;
  ui_component: PriceRenderedUiComponent;
};

type Content = PriceRenderedContent;

type TrackingData = {
  requestId: string;
};

export const usePriceConsistencyMonitoringEvents = () => {
  const { trackExternal } = useTrackerContext();
  const routName = useRouteName();
  const { requestId } = useGlobalContext();

  return useCallback(
    (
      uiComponent: PriceRenderedUiComponent,
      productId: string,
      productPrices: PriceRenderedProductPrice[],
      totalPrice: UIPrice,
      isPaymentPriceChange?: boolean
    ) => {
      const trackingData: TrackingData = { requestId };
      const content = getContent(
        isPaymentPriceChange ? "checkout-payment-price-change" : routName,
        trackingData,
        uiComponent,
        productId,
        productPrices,
        totalPrice
      );

      trackExternal({
        type: "c360",
        args: [
          {
            action_name: ACTION_NAME,
            action_version: ACTION_VERSION,
            content
          }
        ]
      });
    },
    [requestId, routName, trackExternal]
  );
};

const getContent = (
  routeName: RouteNameWithPaymentPriceChange,
  trackingData: TrackingData,
  uiComponent: PriceRenderedUiComponent,
  productId: string,
  productPrices: PriceRenderedProductPrice[],
  totalPrice: UIPrice
): Content => {
  const products: PriceRenderedProduct[] = productPrices.map(({ productPrice, productType }) => ({
    price: {
      currency_code: productPrice.currencyCode,
      price: String(pricingUtils.priceToValue(productPrice))
    },
    product_id: productId,
    product_type: productType
  }));
  const total = {
    currency_code: totalPrice.currencyCode,
    price: String(pricingUtils.priceToValue(totalPrice))
  };

  return {
    funnel_page: routeName,
    products,
    request_id: trackingData.requestId,
    total_price: total,
    ui_component: uiComponent
  };
};

export default usePriceConsistencyMonitoringEvents;
